import { API } from "@swivl-mono/api-helper";
import { BrainType, CoreType, NLPComparisonJobState, NLPComparisonJobStatus, NLPComparisonMatchStatus, NLPModelCache } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize } from "@swivl-mono/ui";
import { useEffect, useRef, useState } from "react";

import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";

interface CLUProject {
  projectName: string;
  projectKind: string;
}

interface CLUDeployment {
  deploymentName: string;
}

type FilterStatus = NLPComparisonMatchStatus | "ALL";

const STATUS_LABELS: Record<NLPComparisonMatchStatus, string> = {
  [NLPComparisonMatchStatus.BOTH_CORRECT]: "Both Correct",
  [NLPComparisonMatchStatus.LUIS_ONLY]: "LUIS Only",
  [NLPComparisonMatchStatus.CLU_ONLY]: "CLU Only",
  [NLPComparisonMatchStatus.BOTH_WRONG]: "Both Wrong",
  [NLPComparisonMatchStatus.PENDING]: "Pending"
};

const STATUS_COLORS: Record<NLPComparisonMatchStatus, string> = {
  [NLPComparisonMatchStatus.BOTH_CORRECT]: "bg-teal-100 text-teal-800",
  [NLPComparisonMatchStatus.LUIS_ONLY]: "bg-orange-100 text-orange-800",
  [NLPComparisonMatchStatus.CLU_ONLY]: "bg-yellow-100 text-yellow-800",
  [NLPComparisonMatchStatus.BOTH_WRONG]: "bg-red-100 text-red-800",
  [NLPComparisonMatchStatus.PENDING]: "bg-grey-100 text-grey-600"
};

export function NLPComparisonView(): JSX.Element {
  // Config state
  const [caches, setCaches] = useState<NLPModelCache[]>([]);
  const [cluProjects, setCluProjects] = useState<CLUProject[]>([]);
  const [cluDeployments, setCluDeployments] = useState<CLUDeployment[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedLuisCache, setSelectedLuisCache] = useState("");
  const [selectedCluProject, setSelectedCluProject] = useState("");
  const [selectedCluDeployment, setSelectedCluDeployment] = useState("");

  // Job state
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobState, setJobState] = useState<NLPComparisonJobState | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Filter state
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("ALL");

  // Load initial data
  useEffect(() => {
    Promise.all([API.NLP.getCacheByCoreType(CoreType.STORAGE), API.NLP.getCLUProjects()])
      .then(([cachesRes, projectsRes]) => {
        setCaches(cachesRes);
        setCluProjects(projectsRes);
      })
      .catch(() => {
        AlertsModel.errorToast("Error loading initial data");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Load deployments when CLU project changes
  useEffect(() => {
    if (!selectedCluProject) {
      setCluDeployments([]);
      setSelectedCluDeployment("");
      return;
    }
    API.NLP.getCLUDeployments(selectedCluProject)
      .then((res) => {
        setCluDeployments(res);
        setSelectedCluDeployment(res.length > 0 ? res[0].deploymentName : "");
      })
      .catch(() => {
        AlertsModel.errorToast("Error loading CLU deployments");
      });
  }, [selectedCluProject]);

  // Poll job status
  useEffect(() => {
    if (!jobId) return;

    pollRef.current = setInterval(async () => {
      try {
        const state = await API.NLP.getComparisonStatus(jobId);
        setJobState(state);
        if (state.status === NLPComparisonJobStatus.COMPLETE || state.status === NLPComparisonJobStatus.ERROR) {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        AlertsModel.errorToast("Error polling comparison status");
        if (pollRef.current) clearInterval(pollRef.current);
      }
    }, 2500);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId]);

  const luisCaches = caches.filter((c) => c.modelType === BrainType.NLP);

  const selectedCache = luisCaches.find((c) => c.id === selectedLuisCache);

  const handleStartComparison = async (): Promise<void> => {
    if (!selectedCache || !selectedCluProject || !selectedCluDeployment) {
      AlertsModel.errorToast("Please select a LUIS model, CLU project, and CLU deployment");
      return;
    }
    setStarting(true);
    setJobState(null);
    setFilterStatus("ALL");
    try {
      const result = await API.NLP.startComparison({
        luisAppId: selectedCache.appId,
        luisVersion: selectedCache.version,
        cluProjectName: selectedCluProject,
        cluDeploymentName: selectedCluDeployment
      });
      setJobId(result.jobId);
    } catch {
      AlertsModel.errorToast("Error starting comparison");
    } finally {
      setStarting(false);
    }
  };

  const isRunning = jobState?.status === NLPComparisonJobStatus.LOADING_UTTERANCES || jobState?.status === NLPComparisonJobStatus.RUNNING_PREDICTIONS;

  const filteredResults = jobState?.results?.filter((r) => filterStatus === "ALL" || r.match === filterStatus) || [];

  return (
    <div className="p-5">
      <h1 className="mb-6 text-3xl text-grey-800">NLP Model Comparison</h1>

      {loading && <p className="text-grey-500">Loading...</p>}

      {!loading && (
        <>
          {/* Configuration */}
          <div className="mb-6 rounded border border-grey-200 p-4">
            <h2 className="mb-4 text-lg font-medium text-grey-800">Configuration</h2>
            <div className="flex flex-wrap items-end gap-6">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-grey-600">LUIS Model</label>
                <select
                  className="rounded border border-grey-200 px-3 py-1.5 text-sm"
                  value={selectedLuisCache}
                  onChange={(e) => setSelectedLuisCache(e.target.value)}
                  disabled={isRunning}
                >
                  <option value="">Select LUIS model...</option>
                  {luisCaches.map((cache) => (
                    <option key={cache.id} value={cache.id}>
                      {cache.name} ({cache.appId} v{cache.version})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-grey-600">CLU Project</label>
                <select
                  className="rounded border border-grey-200 px-3 py-1.5 text-sm"
                  value={selectedCluProject}
                  onChange={(e) => setSelectedCluProject(e.target.value)}
                  disabled={isRunning}
                >
                  <option value="">Select CLU project...</option>
                  {cluProjects.map((p) => (
                    <option key={p.projectName} value={p.projectName}>
                      {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-grey-600">CLU Deployment</label>
                <select
                  className="rounded border border-grey-200 px-3 py-1.5 text-sm"
                  value={selectedCluDeployment}
                  onChange={(e) => setSelectedCluDeployment(e.target.value)}
                  disabled={isRunning || !selectedCluProject}
                >
                  <option value="">Select deployment...</option>
                  {cluDeployments.map((d) => (
                    <option key={d.deploymentName} value={d.deploymentName}>
                      {d.deploymentName}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                color={ButtonColor.ORANGE}
                size={ButtonSize.SMALL}
                loading={starting || isRunning}
                onClick={handleStartComparison}
                disabled={!selectedLuisCache || !selectedCluProject || !selectedCluDeployment}
              >
                Start Comparison
              </Button>
            </div>
          </div>

          {/* Progress */}
          {isRunning && jobState && (
            <div className="mb-6">
              <div className="mb-2 flex items-center justify-between text-sm text-grey-600">
                <span>
                  {jobState.status === NLPComparisonJobStatus.LOADING_UTTERANCES
                    ? "Loading utterances from LUIS..."
                    : `Processing ${jobState.completedUtterances} of ${jobState.totalUtterances} utterances`}
                </span>
                <span>{jobState.progress}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-grey-200">
                <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${jobState.progress}%` }} />
              </div>
            </div>
          )}

          {/* Error */}
          {jobState?.status === NLPComparisonJobStatus.ERROR && (
            <div className="mb-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">Error: {jobState.error || "Unknown error"}</div>
          )}

          {/* Summary */}
          {jobState?.summary && (
            <div className="mb-6 grid grid-cols-5 gap-3">
              <div className="rounded border border-grey-200 bg-grey-50 p-3 text-center">
                <div className="text-2xl font-bold text-grey-800">{jobState.summary.total}</div>
                <div className="text-xs text-grey-500">Total</div>
              </div>
              <div className="rounded border border-teal-200 bg-teal-50 p-3 text-center">
                <div className="text-2xl font-bold text-teal-800">{jobState.summary.bothCorrect}</div>
                <div className="text-xs text-teal-600">
                  Both Correct ({jobState.summary.total > 0 ? Math.round((jobState.summary.bothCorrect / jobState.summary.total) * 100) : 0}%)
                </div>
              </div>
              <div className="rounded border border-orange-200 bg-orange-50 p-3 text-center">
                <div className="text-2xl font-bold text-orange-800">{jobState.summary.luisOnly}</div>
                <div className="text-xs text-orange-600">
                  LUIS Only ({jobState.summary.total > 0 ? Math.round((jobState.summary.luisOnly / jobState.summary.total) * 100) : 0}%)
                </div>
              </div>
              <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-center">
                <div className="text-2xl font-bold text-yellow-800">{jobState.summary.cluOnly}</div>
                <div className="text-xs text-yellow-600">
                  CLU Only ({jobState.summary.total > 0 ? Math.round((jobState.summary.cluOnly / jobState.summary.total) * 100) : 0}%)
                </div>
              </div>
              <div className="rounded border border-red-200 bg-red-50 p-3 text-center">
                <div className="text-2xl font-bold text-red-800">{jobState.summary.bothWrong}</div>
                <div className="text-xs text-red-600">
                  Both Wrong ({jobState.summary.total > 0 ? Math.round((jobState.summary.bothWrong / jobState.summary.total) * 100) : 0}%)
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {jobState?.results && jobState.results.length > 0 && (
            <>
              {/* Filter bar */}
              <div className="mb-4 flex gap-2">
                {(["ALL", ...Object.values(NLPComparisonMatchStatus).filter((s) => s !== NLPComparisonMatchStatus.PENDING)] as FilterStatus[]).map(
                  (status) => (
                    <button
                      key={status}
                      className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                        filterStatus === status ? "bg-grey-800 text-white" : "bg-grey-100 text-grey-600 hover:bg-grey-200"
                      }`}
                      onClick={() => setFilterStatus(status)}
                    >
                      {status === "ALL"
                        ? `All (${jobState.results.length})`
                        : `${STATUS_LABELS[status]} (${jobState.results.filter((r) => r.match === status).length})`}
                    </button>
                  )
                )}
              </div>

              {/* Results table */}
              <div className="max-h-[600px] overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-grey-200 text-grey-500">
                      <th className="px-3 py-2">Utterance</th>
                      <th className="px-3 py-2">Labeled Intent</th>
                      <th className="px-3 py-2">LUIS Predicted</th>
                      <th className="px-3 py-2">LUIS Score</th>
                      <th className="px-3 py-2">CLU Predicted</th>
                      <th className="px-3 py-2">CLU Score</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((result, idx) => (
                      <tr key={idx} className="border-b border-grey-100">
                        <td className="max-w-[300px] truncate px-3 py-2 text-grey-800" title={result.text}>
                          {result.text}
                        </td>
                        <td className="px-3 py-2 font-medium text-grey-700">{result.labeledIntent}</td>
                        <td className={`px-3 py-2 ${result.luisResult?.topIntent === result.labeledIntent ? "text-teal-700" : "text-red-600"}`}>
                          {result.luisResult?.topIntent || "-"}
                        </td>
                        <td className="px-3 py-2 text-grey-500">
                          {result.luisResult?.topScore != null ? result.luisResult.topScore.toFixed(3) : "-"}
                        </td>
                        <td className={`px-3 py-2 ${result.cluResult?.topIntent === result.labeledIntent ? "text-teal-700" : "text-red-600"}`}>
                          {result.cluResult?.topIntent || "-"}
                        </td>
                        <td className="px-3 py-2 text-grey-500">{result.cluResult?.topScore != null ? result.cluResult.topScore.toFixed(3) : "-"}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[result.match]}`}>
                            {STATUS_LABELS[result.match]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
